/**
 * @license Copyright (c) 2003-2018, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md.
 */
import 'ckeditor5/ckeditor5.css';
import 'ckeditor5-premium-features/ckeditor5-premium-features.css';

import {
	AdjacentListsSupport,
	Alignment,
	Autoformat,
	BlockQuote,
	Bold,
	BalloonToolbar,
	CKFinder,
	CKFinderUploadAdapter,
	CloudServices,
	DecoupledEditor,
	EasyImage,
	Emoji,
	MediaEmbed,
	MediaEmbedResize,
	MediaEmbedStyle,
	MediaEmbedToolbar,
	Essentials,
	List,
	ListProperties,
	Font,
	Heading,
	HorizontalLine,
	Image,
	ImageCaption,
	ImageResize,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	Indent,
	IndentBlock,
	Italic,
	Link,
	Mention,
	PageBreak,
	Paragraph,
	PasteFromOffice,
	SimpleUploadAdapter,
	SpecialCharacters,
	SpecialCharactersEssentials,
	Strikethrough,
	Table,
	TableCellProperties,
	TableProperties,
	TableToolbar,
	TextTransformation,
	Underline,
	Fullscreen,
	GeneralHtmlSupport,
} from 'ckeditor5';

import {
	AIChat,
	AIEditorIntegration,
	AIQuickActions,
	AIReviewMode,
	AITranslate,
	AIChatShortcuts,
	CaseChange,
	Comments,
	DocumentOutline,
	ExportPdf,
	ExportWord,
	FormatPainter,
	ImportWord,
	MultiLevelList,
	OpenAITextAdapter,
	Pagination,
	PasteFromOfficeEnhanced,
	RealTimeCollaborativeComments,
	RealTimeCollaborativeEditing,
	RealTimeCollaborativeTrackChanges,
	PresenceList,
	RealTimeCollaborativeRevisionHistory,
	RevisionHistory,
	TableOfContents,
	Template,
	TrackChanges,
	LineHeight,
	Footnotes,
	TrackChangesPreview,
	TrackChangesData,
} from 'ckeditor5-premium-features';

import { generateCommentsAndChanges } from './generate-initial-data';

import {
	EXPORT_HORIZONTAL_SPACE,
	EXPORT_VERTICAL_SPACE,
	CS_CONFIG,
	TEMPLATE_DEFINITIONS,
	LICENSE_KEY,
	HTML_SUPPORT_CONFIG,
	IMPORT_CONFIG,
	OPENAI_AUTHORIZATION,
} from './common';

class RevisionEditor extends DecoupledEditor {}
class RevisionEditorRTC extends DecoupledEditor {}

RevisionEditorRTC.builtinPlugins = [
	AdjacentListsSupport,
	AIChat,
	AIReviewMode,
	AITranslate,
	AIEditorIntegration,
	AIQuickActions,
	AIChatShortcuts,
	OpenAITextAdapter,
	CloudServices,
	Emoji,
	Essentials,
	Alignment,
	Autoformat,
	BalloonToolbar,
	BlockQuote,
	Bold,
	Comments,
	CaseChange,
	CKFinder,
	CKFinderUploadAdapter,
	TrackChanges,
	TrackChangesPreview,
	TrackChangesData,
	RealTimeCollaborativeComments,
	RealTimeCollaborativeEditing,
	RealTimeCollaborativeTrackChanges,
	List,
	ListProperties,
	EasyImage,
	ExportPdf,
	ExportWord,
	Font,
	Fullscreen,
	GeneralHtmlSupport,
	Heading,
	HorizontalLine,
	MediaEmbed,
	MediaEmbedResize,
	MediaEmbedStyle,
	MediaEmbedToolbar,
	Image,
	ImageCaption,
	ImageResize,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	ImportWord,
	Indent,
	IndentBlock,
	Italic,
	Link,
	Mention,
	MultiLevelList,
	PageBreak,
	Paragraph,
	PasteFromOffice,
	SpecialCharacters,
	SpecialCharactersEssentials,
	Strikethrough,
	Table,
	TableCellProperties,
	TableProperties,
	TableToolbar,
	TextTransformation,
	Underline,
	SimpleUploadAdapter,
	Pagination,
	PasteFromOffice,
	PasteFromOfficeEnhanced,
	PresenceList,
	RealTimeCollaborativeRevisionHistory,
	RevisionHistory,
	Template,
	FormatPainter,
	TableOfContents,
	DocumentOutline,
	LineHeight,
	Footnotes,
];

RevisionEditorRTC.defaultConfig = {
	cloudServices: CS_CONFIG,
	trackChangesData: {
		editorCreator: (config, createElement) => {
			if (!config.removePlugins) {
				config.removePlugins = [];
			}

			config.removePlugins.push(
				'AIChat',
				'AITranslate',
				'AIEditorIntegration',
				'AIQuickActions',
				'AIReviewMode',
				'AIChatShortcuts',
				'DocumentOutlineToggler'
			);
			return RevisionEditorRTC.create({
				...config,
				root: {
					...config.root,
					element: createElement(),
				},
			});
		},
	},
	balloonToolbar: ['bold', 'italic', 'link', 'aiQuickActions'],
	toolbar: {
		items: [
			'revisionHistory',
			'trackChanges',
			'comment',
			'commentsArchive',
			'|',
			'toggleAi',
			'aiQuickActions',
			'|',
			'exportPdf',
			'exportWord',
			'importWord',
			'|',
			'previousPage',
			'nextPage',
			'pageNavigation',
			'pageBreak',
			'|',
			'link',
			'insertFootnote',
			'blockquote',
			'uploadImage',
			'insertTable',
			'specialCharacters',
			'horizontalLine',
			'emoji',
			'mediaEmbed',
			'|',
			'multiLevelList',
			'numberedList',
			'bulletedList',
			'|',
			'indent',
			'outdent',
			'|',
			'undo',
			'redo',
			'-',
			'heading',
			'|',
			'fontSize',
			'fontFamily',
			'fontColor',
			'fontBackgroundColor',
			'|',
			'bold',
			'italic',
			'underline',
			'strikethrough',
			'|',
			'alignment',
			'lineHeight',
			'|',
			'insertTemplate',
			'tableOfContents',
			'|',
			'formatPainter',
			'caseChange',
			'|',
			'fullscreen',
		],
		shouldNotGroupWhenFull: true,
	},
	htmlSupport: {
		allow: [
			{
				name: /^.*$/u,
				styles: true,
				classes: true,
				attributes: true,
			},
		],
	},
	heading: {
		options: [
			{
				model: 'paragraph',
				title: 'Paragraph',
				class: 'ck-heading_paragraph',
			},
			{
				model: 'heading1',
				view: 'h1',
				title: 'Heading 1',
				class: 'ck-heading_heading1',
			},
			{
				model: 'heading2',
				view: 'h2',
				title: 'Heading 2',
				class: 'ck-heading_heading2',
			},
			{
				model: 'heading3',
				view: 'h3',
				title: 'Heading 3',
				class: 'ck-heading_heading3',
			},
			{
				model: 'heading4',
				view: 'h4',
				title: 'Heading 4',
				class: 'ck-heading_heading4',
			},
			{
				model: 'heading5',
				view: 'h5',
				title: 'Heading 5',
				class: 'ck-heading_heading5',
			},
			{
				model: 'heading6',
				view: 'h6',
				title: 'Heading 6',
				class: 'ck-heading_heading6',
			},
		],
	},
	mediaEmbed: {
		toolbar: ['mediaEmbed:wrapText', 'mediaEmbed:breakText', 'comment'],
	},
	emoji: {
		definitionsUrl: 'cdn',
	},
	image: {
		toolbar: [
			'toggleImageCaption',
			'imageStyle:inline',
			'imageStyle:wrapText',
			'imageStyle:breakText',
		],
		styles: ['inline', 'block', 'side'],
	},
	table: {
		contentToolbar: [
			'tableColumn',
			'tableRow',
			'mergeTableCells',
			'tableProperties',
			'tableCellProperties',
		],
	},
	indentBlock: {
		offset: 1,
		unit: 'em',
	},
	language: 'en',
	fontFamily: {
		supportAllValues: true,
	},
	fontSize: {
		options: [10, 12, 14, 'default', 18, 20, 22],
		supportAllValues: true,
	},
	list: {
		enableSkipLevelLists: true,
		properties: {
			styles: true,
			startIndex: true,
			reversed: true,
		},
	},
	pagination: {
		// A4
		pageWidth: '21cm',
		pageHeight: '29.7cm',

		pageMargins: {
			top: EXPORT_VERTICAL_SPACE,
			bottom: EXPORT_VERTICAL_SPACE,
			right: EXPORT_HORIZONTAL_SPACE,
			left: EXPORT_HORIZONTAL_SPACE,
		},
	},
	exportPdf: {
		stylesheets: [
			'/assets/styles/export-demo-fonts-cke5.css',
			'EDITOR_STYLES',
			'/assets/styles/export-demo-cke5.css',
		],
		appID: 'cke5-manual-demo',
		converterOptions: {
			document: {
				size: 'A4',
				orientation: 'portrait',
				margins: {
					top: EXPORT_VERTICAL_SPACE,
					bottom: EXPORT_VERTICAL_SPACE,
					right: EXPORT_HORIZONTAL_SPACE,
					left: EXPORT_HORIZONTAL_SPACE,
				},
			},
		},
		dataCallback: (editor) =>
			editor.getData({
				showSuggestionHighlights: true,
				showCommentHighlights: true,
			}),
	},
	exportWord: {
		version: 2,
		stylesheets: ['EDITOR_STYLES', '/assets/styles/export-word-cke5.css'],
		fileName: 'my-file.docx',
		converterOptions: {
			document: {
				size: 'A4', // Default value, you don't need to specify it explicitly for A4.
				margins: {
					top: EXPORT_VERTICAL_SPACE,
					bottom: EXPORT_VERTICAL_SPACE,
					right: EXPORT_HORIZONTAL_SPACE,
					left: EXPORT_HORIZONTAL_SPACE,
				},
			},
		},
	},
	importWord: {
		converterUrl: IMPORT_CONFIG.converterUrl,
		formatting: {
			resets: 'none',
			defaults: 'inline',
			styles: 'inline',
			comments: 'basic',
		},
		collaborationFeatures: {
			userId: 'tester',
			comments: true,
			trackChanges: true,
		},
	},
	trackChanges: {
		showAISource: 'pill',
		preview: {
			renderFunction: (container, elements) => {
				container.classList.add('formatted');

				for (const element of elements) {
					container.appendChild(element);
				}
			},
		},
	},
	comments: {
		editorConfig: {
			extraPlugins: [Autoformat, Bold, Italic, List, Mention],
		},
	},
	licenseKey: LICENSE_KEY,
	template: {
		definitions: TEMPLATE_DEFINITIONS,
	},
	documentOutline: {
		container: document.querySelector('.demo-collaboration-rtc__outline'),
	},
	ai: {
		serviceUrl: 'https://ai.cke-cs.com/v1',
		container: {
			type: 'sidebar',
			element: document.querySelector(
				'.demo-collaboration-rtc__container'
			),
			showResizeButton: false,
			visibleByDefault: false,
		},
		chat: {
			context: {
				alwaysAddSelection: true,
				document: {
					enabled: true,
				},
				urls: {
					enabled: true,
				},
				files: {
					enabled: true,
				},
			},
			shortcuts: [
				{
					id: 'summarize-document',
					type: 'chat',
					label: 'Summarize the document',
					prompt:
						'Summarize the following document in 5-7 sentences. Focus on the main ideas and essential details. ' +
						'Exclude examples, repetition, and minor points. Do not introduce new information.',
				},
				{
					id: 'continue-writing',
					type: 'chat',
					label: 'Continue writing',
					prompt:
						'Continue writing this document. Match the existing tone, vocabulary level, and formatting. ' +
						'Do not repeat or summarize earlier sections. Ensure logical flow and progression of ideas. Add approximately 3 paragraphs.',
					useReasoning: true,
					useWebSearch: true,
				},
				{
					id: 'rewrite-document',
					type: 'chat',
					label: 'Rewrite the document',
					prompt: `Rewrite the document below for the following audience:

Audience: [e.g. Product / Engineering /Leadership]
Primary concern: [e.g., escalations, integrations, customer sentiment]
Context: [e.g. Internal performance review]

Guidelines:

- Emphasize sections most relevant to this audience
- De-emphasize or condense less relevant details
- Adjust terminology to match how this team thinks and speaks
- Keep metrics accurate and unchanged

Tone: [e.g. Clear, practical, collaborative]`,
					useReasoning: true,
					draftMode: true,
				},
				{
					id: 'fix-grammar-and-spelling',
					type: 'review',
					label: 'Fix grammar and spelling',
					commandId: 'correctness',
				},
				{
					id: 'review-document',
					type: 'review',
					label: 'Review document',
				},
				{
					id: 'translate-document',
					type: 'translate',
					label: 'Translate document',
				},
			],
		},
		assistant: {
			adapter: {
				openAI: {
					apiUrl: 'https://openai.public-demo.proxy.ai.cke-cs.com/v1/chat/completions',
					requestHeaders: {
						// eslint-disable-next-line max-len
						Authorization: OPENAI_AUTHORIZATION,
					},
					requestParameters: {
						model: 'gpt-4o',
						max_tokens: 4000,
						stream: true,
					},
				},
			},
			contentAreaCssClass: 'formatted',
		},
		review: {
			extraCommands: [
				{
					id: 'company-style-guide',
					label: 'Company style guide',
					description:
						'Apply the company writing style guide to ensure consistent, professional language.',
					prompt:
						'Apply the following company style guide rules to the text. For each violation, suggest a concrete rewrite. ' +
						'Replace hedging phrases (e.g., "may require", "could positively impact", ' +
						'"should be treated as") with direct, confident statements. ' +
						'Convert passive voice to active voice where the actor is known or implied. ' +
						'Remove filler words and redundant qualifiers (e.g., "overall", "generally", "in terms of"). ' +
						'Replace vague language with precise wording (e.g., "minor improvement" → state the actual change). ' +
						'Keep all data, metrics, and factual content unchanged.',
				},
			],
		},
	},
	caseChange: {
		titleCase: {
			excludeWords: [
				'a',
				'an',
				'and',
				'as',
				'at',
				'but',
				'by',
				'en',
				'for',
				'if',
				'in',
				'nor',
				'of',
				'on',
				'or',
				'per',
				'the',
				'to',
				'vs',
				'vs.',
				'via',
			],
		},
	},
};

RevisionEditor.builtinPlugins = [
	AdjacentListsSupport,
	AIChat,
	AIReviewMode,
	AITranslate,
	AIEditorIntegration,
	AIQuickActions,
	AIChatShortcuts,
	OpenAITextAdapter,
	CloudServices,
	Essentials,
	Alignment,
	Autoformat,
	BalloonToolbar,
	BlockQuote,
	Bold,
	Comments,
	CaseChange,
	CKFinder,
	CKFinderUploadAdapter,
	List,
	ListProperties,
	TrackChanges,
	TrackChangesPreview,
	TrackChangesData,
	EasyImage,
	Emoji,
	ExportPdf,
	ExportWord,
	Font,
	Fullscreen,
	Heading,
	HorizontalLine,
	GeneralHtmlSupport,
	Image,
	ImageCaption,
	ImageResize,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	Indent,
	IndentBlock,
	ImportWord,
	Italic,
	Link,
	Mention,
	MultiLevelList,
	MediaEmbed,
	MediaEmbedResize,
	MediaEmbedStyle,
	MediaEmbedToolbar,
	PageBreak,
	Paragraph,
	PasteFromOffice,
	PasteFromOfficeEnhanced,
	SpecialCharacters,
	SpecialCharactersEssentials,
	Strikethrough,
	Table,
	TableCellProperties,
	TableProperties,
	TableToolbar,
	TextTransformation,
	Underline,
	SimpleUploadAdapter,
	Pagination,
	RevisionHistory,
	Template,
	FormatPainter,
	TableOfContents,
	DocumentOutline,
	LineHeight,
	Footnotes,
];

RevisionEditor.defaultConfig = {
	cloudServices: CS_CONFIG,
	trackChangesData: {
		editorCreator: (config, createElement) => {
			if (!config.removePlugins) {
				config.removePlugins = [];
			}

			config.removePlugins.push(
				'AIChat',
				'AITranslate',
				'AIEditorIntegration',
				'AIQuickActions',
				'AIReviewMode',
				'AIChatShortcuts',
				'DocumentOutlineToggler'
			);
			return RevisionEditor.create({
				...config,
				root: {
					...config.root,
					element: createElement(),
				},
			});
		},
	},
	balloonToolbar: ['bold', 'italic', 'link', 'aiQuickActions'],
	toolbar: {
		items: [
			'revisionHistory',
			'trackChanges',
			'comment',
			'commentsArchive',
			'|',
			'toggleAi',
			'aiQuickActions',
			'|',
			'exportPdf',
			'exportWord',
			'importWord',
			'|',
			'previousPage',
			'nextPage',
			'pageNavigation',
			'pageBreak',
			'|',
			'link',
			'insertFootnote',
			'blockquote',
			'uploadImage',
			'insertTable',
			'emoji',
			'mediaEmbed',
			'specialCharacters',
			'horizontalLine',
			'|',
			'multiLevelList',
			'numberedList',
			'bulletedList',
			'|',
			'indent',
			'outdent',
			'|',
			'undo',
			'redo',
			'-',
			'heading',
			'|',
			'fontSize',
			'fontFamily',
			'fontColor',
			'fontBackgroundColor',
			'|',
			'bold',
			'italic',
			'underline',
			'strikethrough',
			'|',
			'alignment',
			'lineHeight',
			'|',
			'insertTemplate',
			'tableOfContents',
			'|',
			'formatPainter',
			'caseChange',
			'|',
			'fullscreen',
		],
		shouldNotGroupWhenFull: true,
	},
	htmlSupport: {
		allow: [
			{
				name: /^.*$/u,
				styles: true,
				classes: true,
				attributes: true,
			},
		],
	},
	heading: {
		options: [
			{
				model: 'paragraph',
				title: 'Paragraph',
				class: 'ck-heading_paragraph',
			},
			{
				model: 'heading1',
				view: 'h1',
				title: 'Heading 1',
				class: 'ck-heading_heading1',
			},
			{
				model: 'heading2',
				view: 'h2',
				title: 'Heading 2',
				class: 'ck-heading_heading2',
			},
			{
				model: 'heading3',
				view: 'h3',
				title: 'Heading 3',
				class: 'ck-heading_heading3',
			},
			{
				model: 'heading4',
				view: 'h4',
				title: 'Heading 4',
				class: 'ck-heading_heading4',
			},
			{
				model: 'heading5',
				view: 'h5',
				title: 'Heading 5',
				class: 'ck-heading_heading5',
			},
			{
				model: 'heading6',
				view: 'h6',
				title: 'Heading 6',
				class: 'ck-heading_heading6',
			},
		],
	},
	mediaEmbed: {
		toolbar: ['mediaEmbed:wrapText', 'mediaEmbed:breakText', 'comment'],
	},
	emoji: {
		definitionsUrl: 'cdn',
	},
	image: {
		toolbar: [
			'toggleImageCaption',
			'imageStyle:inline',
			'imageStyle:wrapText',
			'imageStyle:breakText',
		],
		styles: ['inline', 'block', 'side'],
	},
	table: {
		contentToolbar: [
			'tableColumn',
			'tableRow',
			'mergeTableCells',
			'tableProperties',
			'tableCellProperties',
		],
	},
	indentBlock: {
		offset: 1,
		unit: 'em',
	},
	language: 'en',
	fontFamily: {
		supportAllValues: true,
	},
	fontSize: {
		options: [10, 12, 14, 'default', 18, 20, 22],
		supportAllValues: true,
	},
	list: {
		enableSkipLevelLists: true,
		properties: {
			styles: true,
			startIndex: true,
			reversed: true,
		},
	},
	pagination: {
		// A4
		pageWidth: '21cm',
		pageHeight: '29.7cm',

		pageMargins: {
			top: EXPORT_VERTICAL_SPACE,
			bottom: EXPORT_VERTICAL_SPACE,
			right: EXPORT_HORIZONTAL_SPACE,
			left: EXPORT_HORIZONTAL_SPACE,
		},
	},
	exportPdf: {
		stylesheets: ['EDITOR_STYLES', '/assets/styles/export-demo-cke5.css'],
		appID: 'cke5-manual-demo',
		converterOptions: {
			document: {
				size: 'A4',
				orientation: 'portrait',
				margins: {
					top: EXPORT_VERTICAL_SPACE,
					bottom: EXPORT_VERTICAL_SPACE,
					right: EXPORT_HORIZONTAL_SPACE,
					left: EXPORT_HORIZONTAL_SPACE,
				},
			},
		},
		dataCallback: (editor) =>
			editor.getData({
				showSuggestionHighlights: true,
				showCommentHighlights: true,
			}),
	},
	exportWord: {
		version: 2,
		stylesheets: ['EDITOR_STYLES', '/assets/styles/export-word-cke5.css'],
		fileName: 'my-file.docx',
		converterOptions: {
			format: 'A4', // Default value, you don't need to specify it explicitly for A4.
			margin_top: EXPORT_VERTICAL_SPACE,
			margin_bottom: EXPORT_VERTICAL_SPACE,
			margin_right: EXPORT_HORIZONTAL_SPACE,
			margin_left: EXPORT_HORIZONTAL_SPACE,
		},
	},
	importWord: {
		converterUrl: IMPORT_CONFIG.converterUrl,
		formatting: {
			resets: 'none',
			defaults: 'inline',
			styles: 'inline',
			comments: 'basic',
		},
		collaborationFeatures: {
			userId: 'tester',
			comments: true,
			trackChanges: true,
		},
	},
	trackChanges: {
		showAISource: 'pill',
		preview: {
			renderFunction: (container, elements) => {
				container.classList.add('formatted');

				for (const element of elements) {
					container.appendChild(element);
				}
			},
		},
	},
	comments: {
		editorConfig: {
			extraPlugins: [Autoformat, Bold, Italic, List, Mention],
		},
	},
	licenseKey: LICENSE_KEY,
	template: {
		definitions: TEMPLATE_DEFINITIONS,
	},
	documentOutline: {
		container: document.querySelector('.demo-collaboration__outline'),
	},
	ai: {
		serviceUrl: 'https://ai.cke-cs.com/v1',
		container: {
			type: 'sidebar',
			element: document.querySelector('.demo-collaboration__container'),
			showResizeButton: false,
			visibleByDefault: false,
		},
		chat: {
			context: {
				alwaysAddSelection: true,
				document: {
					enabled: true,
				},
				urls: {
					enabled: true,
				},
				files: {
					enabled: true,
				},
			},
			shortcuts: [
				{
					id: 'summarize-document',
					type: 'chat',
					label: 'Summarize the document',
					prompt:
						'Summarize the following document in 5-7 sentences. Focus on the main ideas and essential details. ' +
						'Exclude examples, repetition, and minor points. Do not introduce new information.',
				},
				{
					id: 'continue-writing',
					type: 'chat',
					label: 'Continue writing',
					prompt:
						'Continue writing this document. Match the existing tone, vocabulary level, and formatting. ' +
						'Do not repeat or summarize earlier sections. Ensure logical flow and progression of ideas. Add approximately 3 paragraphs.',
					useReasoning: true,
					useWebSearch: true,
				},
				{
					id: 'rewrite-document',
					type: 'chat',
					label: 'Rewrite the document',
					prompt: `Rewrite the document below for the following audience:

Audience: [e.g. Product / Engineering /Leadership]
Primary concern: [e.g., escalations, integrations, customer sentiment]
Context: [e.g. Internal performance review]

Guidelines:

- Emphasize sections most relevant to this audience
- De-emphasize or condense less relevant details
- Adjust terminology to match how this team thinks and speaks
- Keep metrics accurate and unchanged

Tone: [e.g. Clear, practical, collaborative]`,
					useReasoning: true,
					draftMode: true,
				},
				{
					id: 'fix-grammar-and-spelling',
					type: 'review',
					label: 'Fix grammar and spelling',
					commandId: 'correctness',
				},
				{
					id: 'review-document',
					type: 'review',
					label: 'Review document',
				},
				{
					id: 'translate-document',
					type: 'translate',
					label: 'Translate document',
				},
			],
		},
		assistant: {
			adapter: {
				openAI: {
					apiUrl: 'https://openai.public-demo.proxy.ai.cke-cs.com/v1/chat/completions',
					requestHeaders: {
						// eslint-disable-next-line max-len
						Authorization: OPENAI_AUTHORIZATION,
					},
					requestParameters: {
						model: 'gpt-4o',
						max_tokens: 4000,
						stream: true,
					},
				},
			},
			contentAreaCssClass: 'formatted',
		},
		review: {
			extraCommands: [
				{
					id: 'company-style-guide',
					label: 'Company style guide',
					description:
						'Apply the company writing style guide to ensure consistent, professional language.',
					prompt:
						'Apply the following company style guide rules to the text. For each violation, suggest a concrete rewrite. ' +
						'Replace hedging phrases (e.g., "may require", "could positively impact", ' +
						'"should be treated as") with direct, confident statements. ' +
						'Convert passive voice to active voice where the actor is known or implied. ' +
						'Remove filler words and redundant qualifiers (e.g., "overall", "generally", "in terms of"). ' +
						'Replace vague language with precise wording (e.g., "minor improvement" → state the actual change). ' +
						'Keep all data, metrics, and factual content unchanged.',
				},
			],
		},
	},
	caseChange: {
		titleCase: {
			excludeWords: [
				'a',
				'an',
				'and',
				'as',
				'at',
				'but',
				'by',
				'en',
				'for',
				'if',
				'in',
				'nor',
				'of',
				'on',
				'or',
				'per',
				'the',
				'to',
				'vs',
				'vs.',
				'via',
			],
		},
	},
};

export default {
	RevisionHistoryRTC: RevisionEditorRTC,
	RevisionHistory: RevisionEditor,
	generateCommentsAndChanges: generateCommentsAndChanges,
};
